import React, { useState } from 'react';
import { FiBarChart2, FiDownload, FiSearch, FiFilter } from 'react-icons/fi';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const AdminResults = () => {
    // Results — empty until real exams are graded
    const [results] = useState([]);

    return (
        <div style={{ padding: '0px 10px', fontFamily: font, maxWidth: 1200, margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 14 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiBarChart2 color={C.primary} /> Examination Results
                    </h1>
                    <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>View published scores and performance analytics.</p>
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                    <button style={{ padding: '10px 16px', borderRadius: 8, background: '#fff', color: C.text, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontFamily: font, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <FiFilter size={16} /> Filters
                    </button>
                    <button style={{ padding: '10px 16px', borderRadius: 8, background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontFamily: font, boxShadow: '0 4px 12px rgba(16,185,129,0.2)' }}>
                        <FiDownload size={16} /> Export All
                    </button>
                </div>
            </div>

            {/* Performance Overview Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 24 }}>
                {[
                    { title: 'Total Exams Published', value: results.length, color: '#3b82f6' },
                    { title: 'Average Institutional Score', value: results.length > 0 ? (results.reduce((s, r) => s + r.avgScore, 0) / results.length).toFixed(1) + '%' : '—', color: C.primary },
                    { title: 'Overall Pass Rate', value: results.length > 0 ? Math.round(results.reduce((s, r) => s + r.passed, 0) / results.reduce((s, r) => s + r.passed + r.failed, 0) * 100) + '%' : '—', color: '#8b5cf6' },
                ].map((stat, i) => (
                    <div key={i} style={{ background: '#fff', padding: 20, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.sub, textTransform: 'uppercase', marginBottom: 8 }}>{stat.title}</div>
                        <div style={{ fontSize: 32, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                    </div>
                ))}
            </div>

            {/* Results Table */}
            <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                <div style={{ padding: '16px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>Recent Exam Results</h2>

                    <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', width: 200 }}>
                        <FiSearch color={C.sub} style={{ marginRight: 8 }} />
                        <input type="text" placeholder="Search exams..." style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontFamily: font, fontSize: 13 }} />
                    </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Examination Name</th>
                                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase' }}>Date</th>
                                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', textAlign: 'center' }}>Avg Score</th>
                                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', textAlign: 'center' }}>Pass / Fail</th>
                                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 700, color: C.sub, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: 40, textAlign: 'center', color: C.sub }}>No results published yet.</td></tr>
                            ) : results.map(r => {
                                const passRate = Math.round((r.passed / (r.passed + r.failed)) * 100);
                                const isGoodScore = r.avgScore >= 70;

                                return (
                                    <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}`, transition: 'background 0.2s' }}>
                                        <td style={{ padding: '16px 20px', fontWeight: 700, fontSize: 15, color: C.text }}>{r.exam}</td>
                                        <td style={{ padding: '16px 20px', fontSize: 14, color: C.sub }}>{r.date}</td>
                                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                            <span style={{
                                                display: 'inline-block', padding: '4px 8px', borderRadius: 6, fontWeight: 700, fontSize: 13,
                                                background: isGoodScore ? '#ecfdf5' : '#fffbeb', color: isGoodScore ? C.primary : C.orange
                                            }}>
                                                {r.avgScore}%
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                                                <span style={{ color: C.primary }}>{r.passed}</span> /
                                                <span style={{ color: C.red }}>{r.failed}</span>
                                                <span style={{ color: C.sub, fontWeight: 500, marginLeft: 4 }}>({passRate}%)</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                                            <button style={{ padding: '6px 12px', borderRadius: 6, background: '#f1f5f9', color: '#475569', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminResults;
